import type { UserSearchResultDto } from "@/api/workTasks";
import { cn } from "@/design-system/lib/utils";

interface Props {
  matches: UserSearchResultDto[];
  highlighted: number;
  /**
   * Optional pinned-to-top option. The id is `null` so callers can use
   * it to model "Unassigned" (or another sentinel) without colliding
   * with a real user id.
   */
  pinned?: { id: null; displayName: string; secondary?: string };
  onSelect: (id: string | null, displayName: string) => void;
  /** id-prefix used for option ids so screen readers can find the
   *  active descendant without colliding with sibling pickers. */
  idPrefix: string;
  /** Customizable so the @mention dropdown can keep its existing
   *  "Mention suggestions" label while the assignee picker uses a
   *  different one. */
  ariaLabel?: string;
}

/**
 * Tasks Module v1.1 — shared dropdown row used by both the assignee
 * picker and the @mention autocomplete on the comment composer. The
 * parent owns the open/close state, debounced query, and keyboard nav;
 * this component just renders the visible list.
 */
export function UserSuggestionList({
  matches, highlighted, pinned, onSelect, idPrefix,
  ariaLabel = "User suggestions",
}: Props) {
  if (matches.length === 0 && !pinned) return null;

  // Pinned option (e.g. "Unassigned") sits at index 0 so the parent's
  // highlighted index lines up with the rendered order.
  return (
    <ul
      role="listbox"
      aria-label={ariaLabel}
      className={cn(
        "absolute z-30 mt-space-1 w-72 max-h-56 overflow-auto",
        "rounded-md border border-border-light bg-white-pure shadow-lg",
      )}
    >
      {pinned && (
        <li role="option" id={`${idPrefix}-opt-0`} aria-selected={highlighted === 0}>
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(null, pinned.displayName);
            }}
            className={cn(
              "w-full text-left px-space-3 py-space-2 flex flex-col",
              "border-b border-border-light",
              highlighted === 0 ? "bg-gold-highlight/20" : "hover:bg-white-warm",
            )}
          >
            <span className="text-small text-onyx font-semibold">
              {pinned.displayName}
            </span>
            {pinned.secondary && (
              <span className="text-label text-gray-mid">{pinned.secondary}</span>
            )}
          </button>
        </li>
      )}
      {matches.map((u, i) => {
        const idx = pinned ? i + 1 : i;
        return (
          <li
            key={u.id}
            role="option"
            id={`${idPrefix}-opt-${idx}`}
            aria-selected={highlighted === idx}
          >
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(u.id, u.displayName);
              }}
              className={cn(
                "w-full text-left px-space-3 py-space-2 flex flex-col",
                highlighted === idx ? "bg-gold-highlight/20" : "hover:bg-white-warm",
              )}
            >
              <span className="text-small text-onyx font-semibold">
                {u.displayName}
              </span>
              {u.email && (
                <span className="text-label text-gray-mid">{u.email}</span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
